<template>
  <div class="usage-log-container">
    <div class="header">
      <h2>{{ $t('usageLog.title') }}</h2>
      <div class="header-actions">
        <el-button 
          type="primary" 
          icon="el-icon-download" 
          size="small"
          @click="handleExport"
          :disabled="logs.length === 0"
        >
          {{ $t('usageLog.exportData') }}
        </el-button>
      </div>
    </div>

    <!-- 筛选条件 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="query" class="search-form">
        <el-form-item :label="$t('usageLog.region') || '区域'">
          <el-select
            v-model="query.groupId"
            placeholder="请选择区域"
            clearable
            style="width: 120px"
            @change="handleSearch"
          >
            <el-option label="国内" :value="1" />
            <el-option label="国外" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('usageLog.userSN')">
          <el-input
            v-model="query.sn"
            :placeholder="$t('usageLog.userSNPlaceholder')"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
        </el-form-item>
        <el-form-item :label="$t('usageLog.timeRange')">
          <el-date-picker
            v-model="query.dateRange"
            type="datetimerange"
            :range-separator="$t('usageLog.to')"
            :start-placeholder="$t('usageLog.startDate')"
            :end-placeholder="$t('usageLog.endDate')"
            value-format="timestamp"
            style="width: 400px"
            @change="handleDateRangeChange"
          />
        </el-form-item>
        <el-form-item :label="$t('usageLog.quickSelect')">
          <el-select 
            v-model="quickTimeRange" 
            :placeholder="$t('usageLog.quickSelectPlaceholder')"
            style="width: 150px"
            @change="handleQuickTimeChange"
            clearable
          >
            <el-option :label="$t('usageLog.today')" value="today"></el-option>
            <el-option :label="$t('usageLog.yesterday')" value="yesterday"></el-option>
            <el-option :label="$t('usageLog.last7days')" value="last7days"></el-option>
            <el-option :label="$t('usageLog.last30days')" value="last30days"></el-option>
            <el-option :label="$t('usageLog.thisMonth')" value="thisMonth"></el-option>
            <el-option :label="$t('usageLog.lastMonth')" value="lastMonth"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch" :loading="loading" icon="el-icon-search">{{ $t('usageLog.query') }}</el-button>
          <el-button @click="handleReset" icon="el-icon-refresh-left">{{ $t('usageLog.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 统计信息卡片 -->
    <el-card class="statistics-card" v-if="query.sn && statistics">
      <div slot="header" class="card-header">
        <span>
          <i class="el-icon-data-analysis"></i>
          {{ $t('usageLog.statistics') }}（{{ $t('usageLog.sn') }}: {{ query.sn }}）
        </span>
      </div>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="stat-item stat-item-asr">
            <div class="stat-icon">
              <i class="el-icon-microphone"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t('usageLog.totalAsrDuration') }}</div>
              <div class="stat-value">{{ formatDuration(statistics.totalAsrDuration) }}</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-item stat-item-llm">
            <div class="stat-icon">
              <i class="el-icon-cpu"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t('usageLog.totalLlmTokens') }}</div>
              <div class="stat-value">{{ formatNumber(statistics.totalLlmTokens) }}</div>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="stat-item stat-item-tts">
            <div class="stat-icon">
              <i class="el-icon-headset"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ $t('usageLog.totalTtsTokens') }}</div>
              <div class="stat-value">{{ formatNumber(statistics.totalTtsTokens) }}</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 数据表格 -->
    <el-card class="table-card">
      <div slot="header" class="table-header">
        <span class="title">
          <i class="el-icon-document"></i>
          {{ $t('usageLog.listTitle') || '使用记录列表' }}
        </span>
        <span class="record-count">共 {{ pagination.total }} 条记录</span>
      </div>
      <div class="table-wrapper">
        <el-table 
          :data="logs" 
          border 
          v-loading="loading" 
          style="width: 100%"
          highlight-current-row
          :empty-text="loading ? $t('common.loading') : $t('common.noData')"
          @sort-change="handleSortChange"
        >
          <el-table-column type="index" width="60" label="#" align="center" />
          <el-table-column 
            prop="sn" 
            :label="$t('usageLog.userSN')" 
            min-width="150" 
            align="center"
            show-overflow-tooltip
          >
            <template slot-scope="scope">
              <el-tag type="info" size="small">{{ scope.row.sn || '—' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="userId"
            :label="$t('usageLog.userId')"
            min-width="120"
            align="center"
            show-overflow-tooltip
          >
            <template slot-scope="scope">
              <span class="time-text">{{ scope.row.userId || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column 
            prop="time" 
            :label="$t('usageLog.usageTime')" 
            min-width="180" 
            align="center"
            sortable="custom"
          >
            <template slot-scope="scope">
              <span class="time-text">{{ formatTimestamp(scope.row.time) }}</span>
            </template>
          </el-table-column>
          <el-table-column 
            prop="asrDuration" 
            :label="$t('usageLog.asrDuration')" 
            min-width="130" 
            align="center"
            sortable="custom"
          >
            <template slot-scope="scope">
              <el-tag v-if="scope.row.asrDuration" type="success" size="small">
                {{ formatDuration(scope.row.asrDuration) }}
              </el-tag>
              <span v-else class="empty-text">—</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('usageLog.llmTokens')" align="center">
            <el-table-column 
              prop="llmInputTokens" 
              :label="$t('usageLog.input')" 
              min-width="90" 
              align="center"
              sortable="custom"
            >
              <template slot-scope="scope">
                <span class="token-text">{{ formatNumber(scope.row.llmInputTokens) }}</span>
              </template>
            </el-table-column>
            <el-table-column 
              prop="llmOutputTokens" 
              :label="$t('usageLog.output')" 
              min-width="90" 
              align="center"
              sortable="custom"
            >
              <template slot-scope="scope">
                <span class="token-text">{{ formatNumber(scope.row.llmOutputTokens) }}</span>
              </template>
            </el-table-column>
            <el-table-column 
              prop="llmTotalTokens" 
              :label="$t('usageLog.total')" 
              min-width="100" 
              align="center"
              sortable="custom"
            >
              <template slot-scope="scope">
                <el-tag type="primary" size="small">
                  {{ formatNumber(scope.row.llmTotalTokens) }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table-column>
          <el-table-column 
            prop="ttsInputTokens" 
            :label="$t('usageLog.ttsTokens')" 
            min-width="120" 
            align="center"
            sortable="custom"
          >
            <template slot-scope="scope">
              <el-tag v-if="scope.row.ttsInputTokens" type="warning" size="small">
                {{ formatNumber(scope.row.ttsInputTokens) }}
              </el-tag>
              <span v-else class="empty-text">—</span>
            </template>
          </el-table-column>
          <el-table-column prop="devMode" label="开发者" width="80" align="center">
            <template slot-scope="scope">{{ scope.row.devMode === 1 ? '是' : '否' }}</template>
          </el-table-column>
          <el-table-column prop="callScene" label="场景" width="110" align="center" show-overflow-tooltip />
          <el-table-column prop="billingAmount" label="扣点" width="100" align="right">
            <template slot-scope="scope">
              <span v-if="scope.row.billingAmount != null">{{ Number(scope.row.billingAmount).toLocaleString('en-US', { maximumFractionDigits: 1 }) }}</span>
              <span v-else class="empty-text">—</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 30, 50, 100]"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '@/network/system/index'

export default {
  name: 'UsageLog',
  data() {
    return {
      loading: false,
      logs: [],
      query: {
        groupId: null,
        sn: '',
        dateRange: null
      },
      quickTimeRange: '',
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      statistics: null,
      sortProp: null,
      sortOrder: null
    }
  },
  watch: {
    'query.sn'(newVal) {
      if (newVal) {
        this.fetchStatistics()
      } else {
        this.statistics = null
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    formatTimestamp(timestamp) {
      if (!timestamp) return '—'
      const d = new Date(timestamp)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    formatDuration(duration) {
      if (!duration && duration !== 0) return '—'
      if (duration === 0) return '0ms'
      // 毫秒转秒
      if (duration < 1000) {
        return `${duration}ms`
      } else if (duration < 60000) {
        return `${(duration / 1000).toFixed(2)}s`
      } else if (duration < 3600000) {
        const minutes = Math.floor(duration / 60000)
        const seconds = ((duration % 60000) / 1000).toFixed(0)
        return `${minutes}分${seconds}秒`
      } else {
        const hours = Math.floor(duration / 3600000)
        const minutes = Math.floor((duration % 3600000) / 60000)
        return `${hours}小时${minutes}分钟`
      }
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      return num.toLocaleString()
    },
    handleQuickTimeChange(value) {
      if (!value) {
        this.query.dateRange = null
        return
      }

      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      let startTime, endTime

      switch (value) {
        case 'today':
          startTime = today.getTime()
          endTime = now.getTime()
          break
        case 'yesterday': {
          const yesterday = new Date(today)
          yesterday.setDate(yesterday.getDate() - 1)
          startTime = yesterday.getTime()
          endTime = today.getTime() - 1
          break
        }
        case 'last7days':
          startTime = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000).getTime()
          endTime = now.getTime()
          break
        case 'last30days':
          startTime = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000).getTime()
          endTime = now.getTime()
          break
        case 'thisMonth':
          startTime = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
          endTime = now.getTime()
          break
        case 'lastMonth': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
          startTime = lastMonth.getTime()
          endTime = lastMonthEnd.getTime()
          break
        }
        default:
          return
      }

      this.query.dateRange = [startTime, endTime]
      this.handleSearch()
    },
    handleDateRangeChange() {
      this.quickTimeRange = ''
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      // 这里可以根据需要实现前端排序或调用后端排序接口
      // 目前先保持原样，如果后端支持排序，可以在这里调用
    },
    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchData()
    },
    async fetchData() {
      this.loading = true
      try {
        const params = {
          currentPage: this.pagination.currentPage,
          pageSize: this.pagination.pageSize
        }

        if (this.query.sn) {
          params.sn = this.query.sn
        }

        if (this.query.groupId !== null && this.query.groupId !== undefined && this.query.groupId !== '') {
          params.groupId = this.query.groupId
        }

        // 处理时间范围（转换为时间戳毫秒）
        if (this.query.dateRange && this.query.dateRange.length === 2) {
          params.startTime = this.query.dateRange[0]
          params.endTime = this.query.dateRange[1]
        }

        const res = await api.getUsageLogList(params)
        if (res.statusCode === 100200 && res.data) {
          this.logs = res.data.list || []
          this.pagination.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '获取使用记录失败')
        }
      } catch (error) {
        console.error('获取使用记录失败:', error)
        this.$message.error('获取使用记录失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    async fetchStatistics() {
      if (!this.query.sn) {
        this.statistics = null
        return
      }

      try {
        const params = { sn: this.query.sn }
        if (this.query.groupId !== null && this.query.groupId !== undefined && this.query.groupId !== '') {
          params.groupId = this.query.groupId
        }
        const res = await api.getUsageLogStatistics(params)
        if (res.statusCode === 100200 && res.data) {
          this.statistics = res.data
        }
      } catch (error) {
        console.error('获取用户统计失败:', error)
        // 不显示错误提示，因为统计信息不是必需的
      }
    },
    handleReset() {
      this.query = {
        groupId: null,
        sn: '',
        dateRange: null
      }
      this.quickTimeRange = ''
      this.pagination.currentPage = 1
      this.statistics = null
      this.sortProp = null
      this.sortOrder = null
      this.fetchData()
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.fetchData()
    },
    handleExport() {
      if (this.logs.length === 0) {
        this.$message.warning('暂无数据可导出')
        return
      }

      try {
        // 导出功能实现
        const { exportJsonToExcel } = require('@/utils/export2Excel')
        const headers = ['序号', '用户SN', '用户ID', '使用时间', 'ASR时长', 'LLM输入Token', 'LLM输出Token', 'LLM总计Token', 'TTS Token']
        const filters = ['sn', 'userId', 'time', 'asrDuration', 'llmInputTokens', 'llmOutputTokens', 'llmTotalTokens', 'ttsInputTokens']
        
        // 处理数据，添加序号和格式化时间、时长
        const exportData = this.logs.map((item, index) => ({
          index: index + 1,
          sn: item.sn || '',
          userId: item.userId || '',
          time: this.formatTimestamp(item.time),
          asrDuration: this.formatDuration(item.asrDuration),
          llmInputTokens: item.llmInputTokens || 0,
          llmOutputTokens: item.llmOutputTokens || 0,
          llmTotalTokens: item.llmTotalTokens || 0,
          ttsInputTokens: item.ttsInputTokens || 0
        }))

        const exportHeaders = ['index', ...filters]
        const filename = `使用记录_${new Date().toISOString().slice(0, 10)}`
        
        exportJsonToExcel(headers, exportData, filename, exportHeaders)
        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.usage-log-container {
  padding: 24px;
  background: #0F172A;
  min-height: calc(100vh - 84px);
  color: #F1F5F9;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: -0.025em;
}

.filter-card,
.statistics-card,
.table-card {
  background: #1E293B !important;
  border: 1px solid #334155 !important;
  border-radius: 16px !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  margin-bottom: 24px;
}

.search-form {
  padding: 8px 0;
}

.card-header, .table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.card-header span, .table-header .title {
  color: #FFFFFF;
  font-weight: 600;
}

.card-header span i, .table-header span.title i {
  margin-right: 8px;
  color: #8B5CF6;
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 24px;
  background: #0F172A;
  border-radius: 12px;
  border: 1px solid #334155;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-item:hover {
  border-color: #8B5CF6;
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.15);
  transform: translateY(-4px);
}

.stat-icon {
  font-size: 32px;
  color: #8B5CF6;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(139, 92, 246, 0.1);
  border-radius: 12px;
}

.stat-label {
  font-size: 14px;
  color: #FFFFFF;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 28px;
  font-weight: 800;
  color: #F8FAFC;
}

.record-count {
  font-size: 14px;
  color: #FFFFFF;
}

.table-wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #334155;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.time-text {
  color: #E2E8F0;
  font-family: monospace;
}

.token-text {
  color: #F1F5F9;
  font-weight: 500;
}

.empty-text {
  color: #64748B;
}

/* Element UI Overrides */
::v-deep .el-card__header {
  border-bottom: 1px solid #334155 !important;
  padding: 18px 24px !important;
}

::v-deep .el-form-item__label {
  color: #FFFFFF !important;
  font-weight: 500;
}

::v-deep .el-table {
  background-color: #1E293B !important;
  color: #FFFFFF !important;
}

::v-deep .el-table::before,
::v-deep .el-table::after {
  display: none !important;
}

::v-deep .el-table th.el-table__cell {
  background: #334155 !important;
  color: #FFFFFF !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #475569 !important;
  border-right: 1px solid #475569 !important;
}

::v-deep .el-table td.el-table__cell {
  background-color: #1E293B !important;
  color: #FFFFFF !important;
  border-bottom: 1px solid #334155 !important;
  border-right: 1px solid #334155 !important;
}

::v-deep .el-table__body tr:hover > td {
  background-color: rgba(51, 65, 85, 0.8) !important;
}

::v-deep .el-table__body tr.current-row > td {
  background-color: rgba(139, 92, 246, 0.3) !important;
}

::v-deep .el-button--primary {
  background: linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%) !important;
  border: none !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 14px 0 rgba(139, 92, 246, 0.3) !important;
}

::v-deep .el-button--primary.is-disabled {
  background: #334155 !important;
  color: #64748B !important;
  box-shadow: none !important;
}

::v-deep .el-input__inner,
::v-deep .el-range-input {
  background-color: #0F172A !important;
  border-color: #334155 !important;
  color: #F1F5F9 !important;
}

::v-deep .el-range-separator {
  color: #94A3B8 !important;
}

::v-deep .el-tag--primary {
  background: rgba(139, 92, 246, 0.1) !important;
  border-color: rgba(139, 92, 246, 0.2) !important;
  color: #A78BFA !important;
}

::v-deep .el-tag--success {
  background: rgba(16, 185, 129, 0.1) !important;
  border-color: rgba(16, 185, 129, 0.2) !important;
  color: #34D399 !important;
}

::v-deep .el-tag--warning {
  background: rgba(245, 158, 11, 0.1) !important;
  border-color: rgba(245, 158, 11, 0.2) !important;
  color: #FBBF24 !important;
}

::v-deep .el-tag--info {
  background: rgba(148, 163, 184, 0.1) !important;
  border-color: rgba(148, 163, 184, 0.2) !important;
  color: #94A3B8 !important;
}

::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background: #8B5CF6 !important;
}

::v-deep .el-pagination.is-background .el-pager li,
::v-deep .el-pagination.is-background .btn-prev,
::v-deep .el-pagination.is-background .btn-next {
  background-color: #1E293B !important;
  border: 1px solid #334155 !important;
  color: #94A3B8 !important;
}
</style>
